import { CART_COOKIE_NAME } from "@/data/cookies";
import type {
  CheckoutRequest,
  CheckoutResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  OrderFindOneResponse,
  OrderSearchRequest,
  OrderSearchResponse,
  ProductFindOneResponse,
  ProductSearchRequest,
  ProductSearchResponse,
} from "@repo/schemas";
import { ENV } from "./env";

export function searchProducts(payload: ProductSearchRequest) {
  return sendRequest<ProductSearchResponse>("/products/search", payload, {
    method: "POST",
  });
}

export function fetchProductById(productId: string) {
  return sendRequest<ProductFindOneResponse>("/products/" + productId, undefined);
}

export function checkout(cart: CheckoutRequest) {
  return sendRequest<CheckoutResponse>("/checkout", cart, {
    method: "POST",
    responseType: "text",
  });
}

export function login(data: LoginRequest) {
  return sendRequest<LoginResponse>("/auth/login", data, {
    method: "POST",
  });
}

export function logout() {
  return sendRequest("/auth/logout", undefined, { method: "POST" });
}

export async function fetchMe() {
  try {
    const response = await sendRequest<MeResponse>("/auth/me");
    return response.user;
  } catch (_e) {
    return null;
  }
}

export function fetchOrderById(orderId: string) {
  return sendRequest<OrderFindOneResponse>("/orders/" + orderId);
}

export function searchOrders(payload: OrderSearchRequest) {
  return sendRequest<OrderSearchResponse>("/orders/search", payload, { method: "POST" });
}

async function sendRequest<TResponse>(
  path: string,
  body?: unknown,
  options?: {
    responseType?: "text" | "json";
    method?: "POST" | "GET" | "DELETE" | "PUT";
  },
) {
  const { responseType = "json", method = "GET" } = options ?? {};
  const baseUrl = ENV.RUNTIME === "server" ? ENV.API_URL : "/api";
  const response = await fetch(baseUrl + path, {
    method,
    ...(typeof body === "object" ? { body: JSON.stringify(body) } : {}),
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(ENV.RUNTIME === "server" ? { Cookie: await getForwardedCookies() } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return (responseType === "json" ? response.json() : response.text()) as Promise<TResponse>;
}

async function getForwardedCookies() {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  const browserCookies = cookieStore.getAll();
  const cookieHeader = browserCookies
    .filter((cookie) => !cookie.name.startsWith("__next") && cookie.name !== CART_COOKIE_NAME)
    .map((cookie) => `${cookie.name}=${cookie.value}`)
    .join("; ");
  return cookieHeader;
}
